import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { RentalManagerPropertyAddressVerificationWebApiHandler_verification } from '@zg-rentals/rental-manager-env';

import { useSelector } from '../selectors/useSelector';
import { SingleFamilyEnterpriseSelectors, UserSelectors } from '../selectors';
import AddressActions from '../actions/AddressActions';
import enterpriseUtils from '../utils/enterpriseUtils';
import accessUtils from '../utils/accessUtils';
import getLocationRestrictionErrorMessage from '../utils/getLocationRestrictionErrorMessageUtils';

import ErrorMessageConstants from '../constants/ErrorMessageConstants';
import PropertyConstants from '../constants/PropertyConstants';

import type { Dispatch } from 'react';
import type AddPropertyConstants from '../constants/AddPropertyConstants';
import type { ListingErrorsReducerProps, ListingErrorsReducerState, ListingReducerProps } from '../reducers/listing';

const {
  PROPERTY_TYPES,
  PROPERTY_TYPES: { COMMUNITY, CONDO },
  LISTING_TYPES: { RENTAL, ROOM },
  LISTING_CODES_BY_TYPE,
  PROPERTY_CODES_BY_TYPE,
  PROPERTY_TYPES_BY_CODE,
} = PropertyConstants;

type PropertyTypes = (typeof PROPERTY_TYPES)[keyof typeof PROPERTY_TYPES];

const useValidateListing = (
  listing: ListingReducerProps,
  setListing: Dispatch<{ type: keyof ListingReducerProps | typeof AddPropertyConstants.UPDATE_ALL; payload: unknown }>,
  errorObj: ListingErrorsReducerState,
  setErrorObj: Dispatch<ListingErrorsReducerProps>,
  setUnitNumberDetected: (data: boolean) => void,
  setIsValidatedWithoutUnitDetected: (data: boolean) => void,
  setPropertyAlreadyPostedWarningMessage: (data: string | Array<JSX.Element | string>) => void,
  setPropertyId?: (data: string) => void,
  setIsImpreciseLocation?: (data: boolean) => void,
) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => UserSelectors.makeUser(state));
  const hasAnyExpiredContract = useSelector((state) => state.singleFamilyEnterprise.hasAnyExpiredContract);
  const contracts = useSelector((state) => SingleFamilyEnterpriseSelectors.makeContracts(state));
  const [paidMultifamilyLocation, setPaidMultifamilyLocation] = useState(false);
  const appConfig = useSelector((state) => state.app.appConfig);
  const canPostForSaleListing = accessUtils.canPostForSaleListing({ appConfig });

  const prevAddress = useRef<{
    address: string;
    unit: string;
    propertyTypeCode: number | null;
  }>({
    address: '',
    unit: '',
    propertyTypeCode: null,
  });

  const verifyIsValidAddress = async (options = { isPromptUnitNumberCheckRequired: false }) => {
    const { address, unit, propertyType } = listing;
    const { isPromptUnitNumberCheckRequired } = options;

    try {
      const propertyTypeCode = propertyType ? PROPERTY_CODES_BY_TYPE[propertyType as PropertyTypes] : null;
      const propertyTypeCodeParam = propertyTypeCode ?? undefined;

      const { isValidatedWithoutUnit, promptForUnit, propertyId, isInaccurateGeoPrecision, unitWithoutLabel } =
        await RentalManagerPropertyAddressVerificationWebApiHandler_verification({
          fullAddress: address,
          unit,
          propertyTypeCode: propertyTypeCodeParam,
        });

      // if the property is valid without a unit number
      if (unit !== '' && isValidatedWithoutUnit) {
        const notChanged =
          prevAddress.current.address === address &&
          prevAddress.current.unit === unit &&
          prevAddress.current.propertyTypeCode === propertyTypeCode;

        if (!notChanged) {
          setIsValidatedWithoutUnitDetected(true);

          prevAddress.current.address = address;
          prevAddress.current.unit = unit || '';
          prevAddress.current.propertyTypeCode = propertyTypeCode;

          return false;
        }

        prevAddress.current.address = '';
        prevAddress.current.unit = '';
        prevAddress.current.propertyTypeCode = null;
      }

      if (isPromptUnitNumberCheckRequired && promptForUnit && !unit) {
        setUnitNumberDetected(true);

        return false;
      }

      setPropertyId?.(propertyId.toString());
      setIsImpreciseLocation?.(isInaccurateGeoPrecision);

      // use normalized unit value from verification response
      if (unitWithoutLabel) {
        setListing({ type: 'unit', payload: unitWithoutLabel });
      }

      return true;
    } catch ({ message }) {
      setErrorObj({ type: 'address', payload: message });
      return false;
    }
  };

  const verifyIsFormCompleted = () => {
    const { address, propertyType, numUnits } = listing;
    const mustSetCustomerId = enterpriseUtils.getMustSetCustomerId(
      listing,
      user,
      paidMultifamilyLocation,
      hasAnyExpiredContract,
      contracts.length > 0,
    );
    const customerErrorMessage =
      contracts.length === 0 ? ErrorMessageConstants.SINGLE_FAMILY_NO_ACTIVE_CONTRACT : 'Select an active contract';
    const newErrorObject = {
      ...errorObj,
      address: address === '' ? 'Enter an address' : errorObj.address || '',
      propertyType: propertyType === '' ? 'Enter a property type' : errorObj.propertyType || '',
      numUnits: numUnits === '' && propertyType === COMMUNITY ? 'Enter number of units' : errorObj.numUnits || '',
      customerId: mustSetCustomerId ? customerErrorMessage : errorObj.customerId || '',
    };

    setErrorObj({ type: 'listing', payload: newErrorObject });
    return Object.values(newErrorObject).every((key) => key === '');
  };

  const verifyCanSubmitForm = async (
    options = {
      isPromptUnitNumberCheckRequired: false,
    },
  ) => {
    const isFormCompleted = verifyIsFormCompleted();
    if (!isFormCompleted) {
      return false;
    }
    const isValidAddress = await verifyIsValidAddress(options);
    return isValidAddress;
  };

  const handleCheckLocationRestriction = async (
    onChangePropertyType: (propertyTypeToShow: string) => void,
    keyName?: keyof ListingReducerProps,
    newValue: boolean | string = '',
  ) => {
    const newListing = {
      ...listing,
    };

    if (keyName) {
      newListing[keyName] = newValue;
    }

    const { address, isRoomForRent, propertyType, unit } = newListing;

    if (!address) {
      return;
    }

    const apiPromises = [
      dispatch(
        AddressActions.getRestrictions({
          address,
          unit,
          listingTypeCode: isRoomForRent ? LISTING_CODES_BY_TYPE[ROOM] : LISTING_CODES_BY_TYPE[RENTAL],
          propertyTypeCode: propertyType ? PROPERTY_CODES_BY_TYPE[propertyType as PropertyTypes] : null,
        }),
      ),
    ];

    // only change property type if it hasn't been changed already
    if (!listing.propertyType) {
      apiPromises.push(
        dispatch(
          AddressActions.getDetails({
            address,
            unit,
          }),
        ),
      );
    }

    return Promise.allSettled(apiPromises)
      .then((result) => {
        // @ts-ignore
        const getRestrictionsResponse = result[0].value;

        const isMultifamily = propertyType === COMMUNITY;
        const errorMessage = getLocationRestrictionErrorMessage(
          getRestrictionsResponse,
          isMultifamily,
          user.isMultifamilyCustomer,
          false,
          user.isSingleFamilyEnterpriseCustomer,
          user.isPostingPathAdmin,
          user.isHousingConnector,
          canPostForSaleListing,
          setPropertyAlreadyPostedWarningMessage,
        );

        setListing({
          type: 'duplicateAccountListingAlias',
          payload: getRestrictionsResponse.duplicateAccountListingAlias,
        });
        if (getRestrictionsResponse.paidMultifamilyLocation !== paidMultifamilyLocation) {
          setPaidMultifamilyLocation(getRestrictionsResponse.paidMultifamilyLocation);
        }

        if (errorMessage) {
          setErrorObj({ type: 'address', payload: errorMessage });

          // if user sees the ErrorMessageConstants.LOCATION_RESTRICTION.MF_REQUIRED message returned by
          // getLocationRestrictionErrorMessage(), then auto set the property to be community,
          // unless the user manually input a unit, which would make it a condo.
          if (getRestrictionsResponse.paidMultifamilyLocation) {
            // check if property type is defined
            if (!newListing.propertyType) {
              // property type is not defined
              const propertyTypeToShow = unit ? CONDO : COMMUNITY;
              setListing({ type: 'propertyType', payload: propertyTypeToShow });
              onChangePropertyType(propertyTypeToShow);
            }
            return;
          }
        }
        // @ts-ignore
        if (result.length > 1 && result[1]?.value) {
          // @ts-ignore
          const { propertyTypeCode } = result[1].value;

          const propertyTypeToShow = propertyTypeCode && PROPERTY_TYPES_BY_CODE[propertyTypeCode];
          if (propertyTypeToShow) {
            setListing({ type: 'propertyType', payload: propertyTypeToShow });
            onChangePropertyType(propertyTypeToShow);
          }
        }
      })
      .catch(() => {
        // if error occurs AddressActions.getRestrictions
        if (listing.duplicateAccountListingAlias) {
          setListing({ type: 'duplicateAccountListingAlias', payload: null });
        }
      })
      .finally(() => keyName && setListing({ type: keyName, payload: newValue }));
  };

  return { verifyCanSubmitForm, handleCheckLocationRestriction };
};

export default useValidateListing;
